import { useEffect, useState } from 'react';
import { Button, Divider, Group, Modal, Stack, Switch } from '@mantine/core';
import styles from './EditMinistrySchedulerModal.module.sass';
import type { MinistrySchedulerView } from '@/types/ministryScheduler';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type EditMinistrySchedulerModalProps = {
  opened: boolean;
  ministrySchedulerData: MinistrySchedulerView | null;
  onClose: () => void;
  onSave: (data: MinistrySchedulerView) => Promise<void> | void;
};

export const EditMinistrySchedulerModal = ({
  opened,
  ministrySchedulerData,
  onClose,
  onSave,
}: EditMinistrySchedulerModalProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (opened && ministrySchedulerData) {
      setIsActive(ministrySchedulerData.isActive);
    }
  }, [opened, ministrySchedulerData]);

  const handleSave = async () => {
    if (!ministrySchedulerData) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedData: MinistrySchedulerView = {
        ...ministrySchedulerData,
        isActive,
      };

      await onSave(updatedData);
    } catch (error: unknown) {
      console.error('Failed to save ministry scheduler data:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Ministry Scheduler Information"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
      size="lg"
    >
      <Stack gap="md" pt="md">
        <Stack gap="xs">
          <Switch
            label="Ministry Scheduler Active"
            labelPosition="left"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
        </Stack>

        <Divider my="xs" />
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving} color="primary">
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
